import React, { useEffect, useMemo, useState } from "react";
import { Card, Row, Col, Typography, Progress, Empty, Spin } from "antd";
import {
  EyeOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  StarOutlined,
} from "@ant-design/icons";
import { useAuth } from "../../contexts/AuthContext";
import { getDashboardData } from "../../services/Dashboard";

const { Title, Text } = Typography;

const UserDashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);

  useEffect(() => {
    const fetchDashboard = async () => {
      if (!user?._id) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const data = await getDashboardData(user._id);
        setDashboardData(data);
      } catch (error) {
        setDashboardData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, [user?._id]);

  const analytics = dashboardData?.analytics || {};
  const totalAnalyses = analytics.totalEyeAnalysis || 0;
  const savedReports = analytics.savedReportCount || 0;
  const recentReports = analytics.recentReports || [];

  const formatDate = (value) => {
    if (!value) return "N/A";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return "N/A";
    return parsed.toLocaleDateString();
  };

  const reportActivityPercent = useMemo(() => {
    if (!totalAnalyses) return 0;
    return Math.round((savedReports / totalAnalyses) * 100);
  }, [savedReports, totalAnalyses]);

  const stats = [
    {
      title: "Total Eye Analyses",
      value: totalAnalyses,
      icon: <EyeOutlined />,
      subtext: "Scans completed",
    },
    {
      title: "Last Analysis Date",
      value: formatDate(analytics.lastAnalysisDate),
      icon: <ClockCircleOutlined />,
      subtext: "Most recent scan",
    },
    {
      title: "Recent Reports",
      value: recentReports.length,
      icon: <StarOutlined />,
      subtext: "Latest entries",
    },
    {
      title: "Saved Reports",
      value: savedReports,
      icon: <FileTextOutlined />,
      subtext: "Generated insights",
    },
  ];

  return (
    <div className="min-h-screen bg-white p-8">
      {/* Header */}
      <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <Title level={3} className="!text-[#1c5a85] !font-semibold">
            Eye Health Dashboard
          </Title>
          <Text className="text-gray-500 text-sm">
            A summary of your recent analyses and health insights.
          </Text>
        </div>
      </div>

      {loading ? (
        <div className="py-20 flex items-center justify-center">
          <Spin size="large" />
        </div>
      ) : (
        <>
      {/* Stats Cards */}
      <Row gutter={[24, 24]}>
        {stats.map((stat, index) => (
          <Col xs={24} sm={12} lg={6} key={index}>
            <Card
              bordered={false}
              className="rounded-2xl shadow-[0_4px_16px_rgba(28,90,133,0.08)] hover:shadow-[0_6px_20px_rgba(28,90,133,0.12)] transition-all duration-300"
            >
              <div className="flex items-start justify-between">
                <div>
                  <Text className="text-gray-500 text-xs uppercase tracking-wide">
                    {stat.title}
                  </Text>
                  <Title level={2} className="!m-0 !text-[#1c5a85] font-semibold">
                    {stat.value}
                  </Title>
                  <Text className="text-gray-400 text-xs">{stat.subtext}</Text>
                </div>
                <div className="bg-[#1c5a85]/10 text-[#1c5a85] p-3 rounded-full text-2xl">
                  {stat.icon}
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Health Progress */}
      <div className="mt-10">
        <Card
          bordered={false}
          className="rounded-2xl shadow-[0_4px_16px_rgba(28,90,133,0.08)] hover:shadow-[0_6px_20px_rgba(28,90,133,0.12)] transition-all duration-300"
        >
          <div className="flex flex-col sm:flex-row justify-between sm:items-center">
            <div>
              <Title level={4} className="!text-[#1c5a85] !mb-1">
                Report Activity
              </Title>
              <Text className="text-gray-500 text-sm">
                Ratio of saved reports to total analyses
              </Text>
            </div>
            <div className="mt-4 sm:mt-0 w-full sm:w-[300px]">
              <Progress
                percent={reportActivityPercent}
                strokeColor="#1c5a85"
                trailColor="#e8f1f7"
                strokeWidth={10}
                format={(percent) => `${percent}%`}
              />
            </div>
          </div>
        </Card>
      </div>

      {/* Reports Section */}
      <div className="mt-10">
        <Card
          bordered={false}
          className="rounded-2xl shadow-[0_4px_16px_rgba(28,90,133,0.08)]"
        >
          <Title level={4} className="!text-[#1c5a85] !mb-4">
            Recent Reports
          </Title>
          {recentReports.length === 0 ? (
            <Empty description="No reports yet" />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {recentReports.map((report, i) => (
                <div
                  key={report._id || i}
                  className="border border-[#e8f1f7] rounded-xl p-4 hover:bg-[#f9fbfc] transition-all"
                >
                  <Text className="block text-sm text-gray-600 font-medium">
                    {report.patientName || `Eye Scan #${i + 1}`}
                  </Text>
                  <Text className="block text-xs text-gray-400">
                    Conducted on {formatDate(report.createdAt || report.date)}
                  </Text>
                  <div className="mt-2 text-[#1c5a85] font-medium text-sm">
                    {report.summary ? "Summary available" : "No summary yet"}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
        </>
      )}
    </div>
  );
};

export default UserDashboard;
